import { deleteSelection } from '@tiptap/pm/commands'

import { IMAGE_SIZE, VIDEO_SIZE } from '@/tiptap/constants/define'
import { isString } from '@/tiptap/utils/utils'
import { ActionButton } from './ActionButton'

// ---- Types (doc only) ----
// BubbleImageFloatType: 'float-left' | 'float-none' | 'float-right'
// BubbleImageOrVideoSizeType: 'size-small' | 'size-medium' | 'size-large'
// BubbleImageType: above + `image-${size}` | `video-${size}` | 'image' | 'image-aspect-ratio' | 'unlink' | 'link-open' | 'remove'
// BubbleVideoType: 'video' | 'remove'
// BubbleTableType: 'table-add-column-before' | 'table-add-column-after' | 'table-delete-column' | 'table-add-row-before' | 'table-add-row-after' | 'table-delete-row' | 'table-merge-or-split-cells' | 'table-delete-table'
// NodeTypeKey: 'table' | 'image' | 'text' | 'link' | 'video'

const imageFloatMenus = (editor) => {
  const types = ['float-left', 'float-none', 'float-right']
  const icons = ['formatFloatLeft', 'formatFloatNone', 'formatFloatRight']
  const display = ['left', 'block', 'right']

  return types.map((float, i) => ({
    type: float,
    component: ActionButton,
    componentProps: {
      tooltip: `editor.image.${float.replace('-', '.')}.tooltip`,
      icon: icons[i],
      action: () => editor.chain().focus().updateImage({ display: display[i] }).run(),
      isActive: () => editor.isActive('image', { display: display[i] })
    }
  }))
}

const imageSizeMenus = (editor) => {
  const types = ['size-small', 'size-medium', 'size-large']
  const icons = ['sizeS', 'sizeM', 'sizeL']

  return types.map((size, i) => ({
    type: `image-${size}`,
    component: ActionButton,
    componentProps: {
      tooltip: `editor.${size.replace('-', '.')}.tooltip`,
      icon: icons[i],
      action: () => editor.chain().focus().updateImage({ width: IMAGE_SIZE[size], height: null }).run(),
      isActive: () => editor.isActive('image', { width: IMAGE_SIZE[size] })
    }
  }))
}

const videoSizeMenus = (editor) => {
  const types = ['size-small', 'size-medium', 'size-large']
  const icons = ['sizeS', 'sizeM', 'sizeL']

  return types.map((size, i) => ({
    type: `video-${size}`,
    component: ActionButton,
    componentProps: {
      tooltip: `editor.${size.replace('-', '.')}.tooltip`,
      icon: icons[i],
      action: () => editor.chain().focus().updateVideo({ width: VIDEO_SIZE[size] }).run(),
      isActive: () => editor.isActive('video', { width: VIDEO_SIZE[size] })
    }
  }))
}

const tableBubbleMenus = (editor) => {
  return [
    {
      type: 'table-add-column-before',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.add_column_before',
        icon: 'tableColumnPlusBefore',
        action: () => editor.chain().focus().addColumnBefore().run(),
        disabled: !editor.can().addColumnBefore()
      }
    },
    {
      type: 'table-add-column-after',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.add_column_after',
        icon: 'tableColumnPlusAfter',
        action: () => editor.chain().focus().addColumnAfter().run(),
        disabled: !editor.can().addColumnAfter()
      }
    },
    {
      type: 'table-delete-column',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.delete_column',
        icon: 'tableColumnRemove',
        action: () => editor.chain().focus().deleteColumn().run(),
        disabled: !editor.can().deleteColumn()
      }
    },
    {
      type: 'table-add-row-before',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.add_row_before',
        icon: 'tableRowPlusBefore',
        action: () => editor.chain().focus().addRowBefore().run(),
        disabled: !editor.can().addRowBefore()
      }
    },
    {
      type: 'table-add-row-after',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.add_row_after',
        icon: 'tableRowPlusAfter',
        action: () => editor.chain().focus().addRowAfter().run(),
        disabled: !editor.can().addRowAfter()
      }
    },
    {
      type: 'table-delete-row',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.delete_row',
        icon: 'tableRowRemove',
        action: () => editor.chain().focus().deleteRow().run(),
        disabled: !editor.can().deleteRow()
      }
    },
    {
      type: 'table-merge-or-split-cells',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.merge_or_split_cells',
        icon: 'tableMergeCells',
        action: () => editor.chain().focus().mergeOrSplit().run(),
        disabled: !editor.can().mergeOrSplit()
      }
    },
    {
      type: 'table-delete-table',
      component: ActionButton,
      componentProps: {
        tooltip: 'editor.table.menu.delete_table',
        icon: 'tableRemove',
        action: () => editor.chain().focus().deleteTable().run(),
        disabled: !editor.can().deleteTable()
      }
    }
  ]
}

export const defaultBubbleList = (editor) => [
  ...imageFloatMenus(editor),
  ...imageSizeMenus(editor),
  ...videoSizeMenus(editor),
  ...tableBubbleMenus(editor),
  {
    type: 'image-aspect-ratio',
    component: ActionButton,
    componentProps: {
      tooltip: 'editor.image.dialog.form.aspectRatio',
      icon: 'aspectRatio',
      action: () => {
        const isLock = editor.isActive('image', { lockAspectRatio: true })
        editor.chain().focus().updateImage({
          lockAspectRatio: !isLock,
          height: isLock ? undefined : null
        }).run()
      },
      isActive: () => editor.isActive('image', { lockAspectRatio: true })
    }
  },
  {
    type: 'unlink',
    component: ActionButton,
    componentProps: {
      tooltip: 'editor.link.unlink.tooltip',
      icon: 'linkVariantOff',
      action: () => {
        const { href } = editor.getAttributes('link')
        editor.chain().extendMarkRange('link', { href }).unsetLink().focus().run()
      }
    }
  },
  {
    type: 'link-open',
    component: ActionButton,
    componentProps: {
      tooltip: 'editor.link.open',
      icon: 'openInNew',
      action: () => {
        const { href } = editor.getAttributes('link')
        if (isString(href) && href) {
          window.open(href, '_blank')
        }
      }
    }
  },
  {
    type: 'remove',
    component: ActionButton,
    componentProps: {
      tooltip: 'editor.remove',
      icon: 'delete',
      action: () => {
        const { state, dispatch } = editor.view
        deleteSelection(state, dispatch)
      }
    }
  }
]

export const generateBubbleTypeMenu = (list, defaultList, { editor, t }) => {
  const { extensions = [] } = editor.extensionManager
  const items = {}

  for (const node of Object.keys(list)) {
    const nodeType = list[node]
    if (!nodeType) continue

    const _items = []

    for (const ext of nodeType) {
      if (ext === 'divider') {
        const lastItem = _items[_items.length - 1]
        if (lastItem?.type === 'divider') continue

        _items.push({ type: 'divider', component: undefined, componentProps: {} })
        continue
      }

      const found = defaultList.find(k => k.type === ext)
      if (found) {
        _items.push({
          ...found,
          componentProps: {
            ...found.componentProps,
            tooltip: found.componentProps.tooltip ? t(found.componentProps.tooltip) : undefined
          },
          componentSlots: found.componentSlots
        })
        continue
      }

      const findExt = extensions.find(k => k.name === ext)
      if (findExt) {
        const { button } = findExt.options || {}
        const _button = button ? button({ editor, extension: findExt, t }) : undefined
        if (_button) {
          _items.push({
            type: ext,
            component: _button.component,
            componentProps: _button.componentProps,
            componentSlots: _button.componentSlots
          })
        }
        continue
      }
    }

    const lastItem = _items[_items.length - 1]
    const firstItem = _items[0]
    if (lastItem?.type === 'divider') _items.pop()
    if (firstItem?.type === 'divider') _items.shift()

    items[node] = _items
  }

  return items
}
